import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A component that powers no-refresh tag filtering and pagination on the blog
 * listing page. It intercepts tag and pagination link clicks, re-renders the
 * section via the Section Rendering API, and keeps the browser URL in sync.
 *
 * The server re-renders active tag states and pagination markup, so this
 * component never toggles classes or mutates DOM directly.
 */
class BlogFilter extends Component {
  /**
   * The bare section ID (e.g. "main"), without the `shopify-section-` prefix.
   *
   * @returns {string} The section ID to re-render.
   */
  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  /**
   * Handles clicks on tag links. Navigates to the tag URL, resetting any
   * pagination state since a new tag always starts on page one.
   *
   * @param {MouseEvent} event - The click event, proxied so `target` is the anchor carrying `on:click`.
   * @returns {void}
   */
  handleTagClick(event) {
    event.preventDefault();

    const anchor = event.target;
    if (!(anchor instanceof HTMLAnchorElement)) return;

    const url = new URL(anchor.href);
    url.searchParams.delete('page');
    url.hash = '';

    this.#navigate(url);
  }

  /**
   * Handles clicks on pagination links. The page number is parsed by the
   * Component base class from `on:click="/handlePageChange?page=N"` and passed
   * as the first argument.
   *
   * @param {{ page: string | number }} data - The parsed data object containing the target page.
   * @param {MouseEvent} event - The click event.
   * @returns {void}
   */
  handlePageChange(data, event) {
    event.preventDefault();

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(data.page));
    url.hash = '';

    this.#navigate(url);
  }

  /**
   * Re-renders the section for the given URL and syncs browser history.
   *
   * @param {URL} url - The URL to render and navigate to.
   * @returns {Promise<void>}
   */
  async #navigate(url) {
    this.setAttribute('aria-busy', 'true');

    try {
      await sectionRenderer.renderSection(this.sectionId, { url });
      history.pushState('', '', url.toString());
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Re-renders the section to match the URL after a history popstate
   * (browser back/forward navigation).
   *
   * @returns {void}
   */
  #handlePopstate = () => {
    sectionRenderer.renderSection(this.sectionId, { url: new URL(window.location.href) });
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this.#handlePopstate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#handlePopstate);
  }
}

if (!customElements.get('blog-filter')) {
  customElements.define('blog-filter', BlogFilter);
}
